import { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { Package, LogIn, ShoppingBag, Sparkles, Download, Trash2, Share2, Truck, Loader2 } from "lucide-react";
import { Link } from "react-router-dom";
import api from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { useCart } from "../context/CartContext";
import { inr } from "../lib/format";
import { shopLook } from "../lib/lookActions";
import { ShareLookMenu } from "../components/ShareLookMenu";
import { TrackingTimeline } from "../components/TrackingTimeline";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../components/ui/dialog";
import { toast } from "sonner";

export default function Account() {
  const { user, loading, login } = useAuth();
  const { addItem } = useCart();
  const [orders, setOrders] = useState([]);
  const [looks, setLooks] = useState([]);
  const [fetched, setFetched] = useState(false);
  const [trackOrder, setTrackOrder] = useState(null);
  const [tracking, setTracking] = useState(null);
  const [trackLoading, setTrackLoading] = useState(false);

  const openTracking = async (o) => {
    setTrackOrder(o); setTracking(null); setTrackLoading(true);
    try {
      const r = await api.get(`/orders/${o.order_id}/tracking`);
      setTracking(r.data);
    } catch (e) {
      console.error("Failed to load tracking:", e);
      toast.error("Could not load tracking right now");
    } finally { setTrackLoading(false); }
  };

  useEffect(() => {
    if (user) {
      api.get("/orders").then((r) => setOrders(r.data)).catch(() => {}).finally(() => setFetched(true));
      api.get("/tryon/looks").then((r) => setLooks(r.data)).catch(() => {});
    }
  }, [user]);

  const deleteLook = async (id) => {
    setLooks((prev) => prev.filter((l) => l.look_id !== id));
    try { await api.delete(`/tryon/looks/${id}`); toast("Look removed"); } catch { toast.error("Could not remove"); }
  };

  const downloadLook = (l) => {
    const a = document.createElement("a");
    a.href = l.image;
    a.download = `tashora-${l.product_name || "look"}.png`;
    a.click();
  };

  if (loading) {
    return <div className="min-h-[60vh] grid place-items-center"><div className="w-8 h-8 border-2 border-gold border-t-transparent rounded-full animate-spin" /></div>;
  }

  if (!user) {
    return (
      <div className="max-w-md mx-auto px-6 py-28 text-center" data-testid="account-signin-prompt">
        <LogIn className="w-10 h-10 text-gold mx-auto" />
        <h1 className="font-serif text-3xl text-gold-light mt-5">Sign in to Tashora</h1>
        <p className="text-muted-foreground mt-2">Access your orders and saved AI try-on looks.</p>
        <button onClick={login} data-testid="account-login-button" className="mt-8 bg-gold text-obsidian font-semibold px-8 py-3 rounded-full hover:bg-gold-light transition-colors">
          Continue with Google
        </button>
      </div>
    );
  }

  return (
    <div className="max-w-[1000px] mx-auto px-4 sm:px-8 lg:px-12 py-12" data-testid="account-page">
      <div className="flex items-center gap-4 mb-10">
        {user.picture && <img src={user.picture} alt="" className="w-14 h-14 rounded-full gold-border object-cover" />}
        <div>
          <h1 className="font-serif text-3xl text-gold-light">{user.name}</h1>
          <p className="text-sm text-muted-foreground">{user.email}</p>
        </div>
      </div>

      <div className="flex items-center gap-2 mb-6">
        <Package className="w-5 h-5 text-gold" />
        <h2 className="font-serif text-2xl text-gold-muted">Order history</h2>
      </div>

      {fetched && orders.length === 0 ? (
        <div className="glass rounded-lg p-12 text-center" data-testid="no-orders">
          <ShoppingBag className="w-8 h-8 text-gold/50 mx-auto" />
          <p className="text-gold-muted mt-4">No orders yet</p>
          <Link to="/collection" className="text-gold text-sm mt-2 inline-block">Explore the collection</Link>
        </div>
      ) : (
        <div className="space-y-4">
          {orders.map((o, i) => (
            <motion.div key={o.order_id} initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: i * 0.05 }}
              className="glass rounded-lg p-5" data-testid={`order-${o.order_id}`}>
              <div className="flex justify-between items-center mb-4">
                <div>
                  <p className="font-mono text-xs text-gold">#{o.order_id.slice(4, 12).toUpperCase()}</p>
                  <p className="text-xs text-muted-foreground">{new Date(o.created_at).toLocaleDateString(undefined, { year: "numeric", month: "long", day: "numeric" })}</p>
                </div>
                <div className="text-right">
                  <span className="text-xs px-2.5 py-1 rounded-full bg-gold/15 text-gold capitalize">{o.status}</span>
                  <p className="font-mono text-lg text-gold mt-1">{inr(o.amount)}</p>
                </div>
              </div>
              <div className="flex gap-3 flex-wrap items-start">
                <div className="flex gap-3 flex-wrap flex-1">
                {o.items?.map((it) => (
                  <div key={it.product_id} className="flex items-center gap-3 bg-obsidian-2/60 rounded-md p-2 pr-4">
                    <img src={it.image} alt="" className="w-12 h-14 object-cover rounded" />
                    <div>
                      <p className="text-sm text-gold-muted">{it.name}</p>
                      <p className="text-xs text-muted-foreground">Qty {it.quantity} · {inr(it.price)}</p>
                    </div>
                  </div>
                ))}
                </div>
                <button onClick={() => openTracking(o)} data-testid={`track-order-${o.order_id}`}
                  className="shrink-0 inline-flex items-center gap-1.5 text-xs border border-gold/40 text-gold rounded-full px-3.5 py-2 hover:bg-gold/10 transition-colors">
                  <Truck className="w-3.5 h-3.5" /> Track order
                </button>
              </div>
            </motion.div>
          ))}
        </div>
      )}

      {/* My Try-On Looks gallery */}
      <div className="flex items-center gap-2 mb-6 mt-16">
        <Sparkles className="w-5 h-5 text-gold" />
        <h2 className="font-serif text-2xl text-gold-muted">My Try-On Looks</h2>
      </div>

      {looks.length === 0 ? (
        <div className="glass rounded-lg p-12 text-center" data-testid="no-looks">
          <Sparkles className="w-8 h-8 text-gold/50 mx-auto" />
          <p className="text-gold-muted mt-4">No saved looks yet</p>
          <Link to="/try-on" className="text-gold text-sm mt-2 inline-block">Open the Draping Studio</Link>
        </div>
      ) : (
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4" data-testid="looks-gallery">
          {looks.map((l, i) => (
            <motion.div key={l.look_id} initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: i * 0.04 }}
              className="group relative rounded-lg overflow-hidden gold-border bg-obsidian-2" data-testid={`account-look-${l.look_id}`}>
              <div className="relative">
                <img src={l.image} alt={l.product_name} className="w-full aspect-[4/5] object-cover" />
                <div className="absolute top-2 right-2 flex gap-1.5 opacity-0 group-hover:opacity-100 transition-opacity">
                  <button onClick={() => downloadLook(l)} data-testid={`look-download-${l.look_id}`} className="w-8 h-8 grid place-items-center rounded-full bg-obsidian/80 text-gold hover:bg-obsidian">
                    <Download className="w-4 h-4" />
                  </button>
                  <ShareLookMenu image={l.image} productName={l.product_name}>
                    <button data-testid={`look-share-${l.look_id}`} className="w-8 h-8 grid place-items-center rounded-full bg-obsidian/80 text-gold hover:bg-obsidian">
                      <Share2 className="w-4 h-4" />
                    </button>
                  </ShareLookMenu>
                  <button onClick={() => deleteLook(l.look_id)} data-testid={`look-delete-${l.look_id}`} className="w-8 h-8 grid place-items-center rounded-full bg-obsidian/80 text-destructive hover:bg-obsidian">
                    <Trash2 className="w-4 h-4" />
                  </button>
                </div>
              </div>
              <p className="text-xs text-gold-muted px-2 pt-2 truncate">{l.product_name}</p>
              {l.product_id && (
                <button onClick={() => shopLook(l.product_id, addItem)} data-testid={`look-shop-${l.look_id}`}
                  className="m-2 w-[calc(100%-1rem)] text-[11px] bg-gold text-obsidian font-semibold py-1.5 rounded hover:bg-gold-light transition-colors flex items-center justify-center gap-1">
                  <ShoppingBag className="w-3 h-3" /> Shop this saree
                </button>
              )}
            </motion.div>
          ))}
        </div>
      )}

      <Dialog open={!!trackOrder} onOpenChange={(v) => { if (!v) setTrackOrder(null); }}>
        <DialogContent className="bg-obsidian-2 border-gold/20 max-w-lg max-h-[85vh] overflow-y-auto" data-testid="tracking-dialog">
          <DialogHeader>
            <DialogTitle className="font-serif text-2xl text-gold-light">
              Tracking · #{trackOrder ? trackOrder.order_id.slice(4, 12).toUpperCase() : ""}
            </DialogTitle>
          </DialogHeader>
          {trackLoading ? (
            <div className="py-12 grid place-items-center"><Loader2 className="w-6 h-6 text-gold animate-spin" /></div>
          ) : tracking ? (
            <TrackingTimeline data={tracking} />
          ) : (
            <p className="text-muted-foreground text-sm py-8 text-center">No tracking available yet.</p>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
